import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import {
  EMPTY_LEADERSHIP_PIPELINE,
  normalizeLeadershipPipelineResponse,
} from '../Utils/partnerLeadershipUtils';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

export const useLeadershipPipeline = (enabled = true) => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [pipeline, setPipeline] = useState(EMPTY_LEADERSHIP_PIPELINE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const withAuthRetry = useCallback(async (apiCall) => {
    const token = resolveAccessToken(accessToken);
    try {
      return await apiCall(token);
    } catch (err) {
      if (!isUnauthorizedError(err) || !Cookies.get('refreshToken')) {
        throw err;
      }

      await dispatch(refreshToken());
      const refreshedToken = Cookies.get('accessToken');
      if (!refreshedToken) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      return apiCall(refreshedToken);
    }
  }, [accessToken, dispatch]);

  const refreshPipeline = useCallback(async () => {
    if (!enabled) {
      setPipeline(EMPTY_LEADERSHIP_PIPELINE);
      setLoading(false);
      return;
    }

    const token = resolveAccessToken(accessToken);
    if (!token) {
      setPipeline(EMPTY_LEADERSHIP_PIPELINE);
      setLoading(false);
      setError('Your session has expired. Please sign in again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await withAuthRetry((activeToken) => (
        partnerPortalApi.fetchLeadershipPipeline(activeToken)
      ));
      setPipeline(normalizeLeadershipPipelineResponse(data));
    } catch (err) {
      setError(err.message);
      setPipeline(EMPTY_LEADERSHIP_PIPELINE);
    } finally {
      setLoading(false);
    }
  }, [accessToken, enabled, withAuthRetry]);

  useEffect(() => {
    refreshPipeline();
  }, [refreshPipeline]);

  return {
    pipeline,
    loading,
    error,
    refreshPipeline,
  };
};
